import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchBanksData } from '../../services/dataApi';
import PageLoader from '../../components/PageLoader';
import BanksTable from '../../components/BanksTable';
import SearchBar from '../../components/SearchBar';
import { searchInData } from '../../utils/searchUtils';
import useUrlPagination from '../../hooks/useUrlPagination';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import '../Page.css';

const AdminBanks = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentPage, setCurrentPage, itemsPerPage } = useUrlPagination();
  const [pagination, setPagination] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 400);

  const loadData = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await fetchBanksData(page, itemsPerPage, debouncedSearchTerm);
      setData(result.data || result);
      setPagination(result.pagination);
    } catch (err) {
      setError(err.message || 'An error occurred while loading data');
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, debouncedSearchTerm]);

  useEffect(() => {
    loadData(currentPage);
  }, [currentPage, loadData]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  // Filter data on client side
  const filteredData = useMemo(() => {
    if (!data) return null;
    
    let banks = [];
    if (data && data.data && Array.isArray(data.data)) {
      banks = data.data;
    } else if (Array.isArray(data)) {
      banks = data;
    } else if (data && data.ops && data.ops.length > 0) {
      const firstOp = data.ops[0];
      if (firstOp.list && Array.isArray(firstOp.list)) {
        banks = firstOp.list;
      } else if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
        banks = firstOp.data.list;
      }
    }

    if (!searchTerm || searchTerm.trim() === '') {
      return data;
    }

    const filtered = searchInData(banks, searchTerm);
    
    if (data && data.data && Array.isArray(data.data)) {
      return { ...data, data: filtered };
    } else if (Array.isArray(data)) {
      return filtered;
    } else {
      return { ...data, ops: [{ ...data.ops[0], list: filtered }] };
    }
  }, [data, searchTerm]);

  const banksCount = pagination?.total ?? (filteredData?.data?.length || 0);

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-heading">
          <h1>Banks</h1>
          <p>Keep the bank directory readable and searchable inside the same admin visual system.</p>
        </div>
        <div className="page-overview">
          <div className="overview-card">
            <div className="overview-label">Banks</div>
            <div className="overview-value">{banksCount}</div>
            <div className="overview-meta">Visible records</div>
          </div>
          <div className="overview-card">
            <div className="overview-label">Catalog</div>
            <div className="overview-value">Reference</div>
            <div className="overview-meta">Operational directory</div>
          </div>
        </div>
      </div>
      <div className="page-content">
        <PageLoader loading={loading} error={error}>
          {data ? (
            <div className="page-data">
              <div className="page-section-header">
                <div className="page-section-title">
                  <h2>Bank Directory</h2>
                  <p>Open a bank to inspect the full raw payload.</p>
                </div>
                <div className="page-section-meta">
                  <div className="page-chip">50 banks per page</div>
                  <div className="page-chip">{searchTerm ? 'Filtered directory' : 'Full directory'}</div>
                </div>
              </div>
              <SearchBar 
                onSearch={handleSearch}
                value={searchTerm}
                resultCount={pagination?.total || 0}
                isUpdating={searchTerm !== debouncedSearchTerm}
                placeholder="Search banks..."
              />
              <BanksTable 
                data={filteredData} 
                pagination={pagination}
                onPageChange={handlePageChange}
              />
            </div>
          ) : (
            <div className="page-placeholder">
              <p>No data received</p>
            </div>
          )}
        </PageLoader>
      </div>
    </div>
  );
};

export default AdminBanks;
